import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, Home, Receipt, Phone, Mail, Smartphone, Clock, Truck } from 'lucide-react';
import { formatCurrencyXCD } from '../utils/currencyHelper';

// iOS navigation bar (inline title + back chevron) for pushed pages.
function IOSNavBar({ title, onBack }) {
  return (
    <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="relative flex items-center px-3 py-2 max-w-lg mx-auto">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="w-9 h-9 rounded-full bg-[#F2F2F7] flex items-center justify-center shrink-0 active:bg-gray-200"
        >
          <ChevronLeft size={20} className="text-gray-700" />
        </button>
        <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold text-gray-900">
          {title}
        </h1>
      </div>
    </header>
  );
}

export default function OrderSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Parse order data from URL parameters or get from localStorage
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
    const redirectStatus = searchParams.get('redirect_status');

    // Get order data from localStorage (saved during checkout)
    const savedOrderData = localStorage.getItem('currentOrder');
    if (savedOrderData) {
      setOrderData(JSON.parse(savedOrderData));
    }

    setLoading(false);

    // Clear cart after successful payment
    if (redirectStatus === 'succeeded') {
      localStorage.removeItem('cart');
      localStorage.removeItem('currentOrder');
    }
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#34C759]/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#34C759]/30 border-t-[#34C759]"></div>
          </div>
          <h2 className="text-[17px] font-semibold text-gray-900 mb-1">Processing Order</h2>
          <p className="text-[15px] text-[#6D6D72]">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <IOSNavBar title="Order Confirmed" onBack={() => navigate('/dashboard/orders')} />

      <div className="max-w-lg mx-auto px-4 py-6 space-y-4 pb-12">
        {/* Success hero — mirrors iOS "Order Placed!" checkmark state */}
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-20 h-20 bg-[#34C759]/15 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={44} className="text-[#34C759]" />
          </div>

          <h2 className="text-[28px] font-bold text-gray-900 mb-2">
            Order Placed!
          </h2>

          <p className="text-[17px] text-[#6D6D72]">
            Your order has been placed and is being prepared.
          </p>
        </div>

        {/* Order Details card */}
        {orderData && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h3 className="text-[17px] font-semibold text-gray-900 px-4 pt-4 pb-2 flex items-center gap-2">
              <Receipt size={18} className="text-[#007AFF]" />
              Order Details
            </h3>
            <div className="divide-y divide-black/[0.06]">
              <div className="flex justify-between px-4 py-3">
                <span className="text-[15px] text-[#6D6D72]">Order Number</span>
                <span className="text-[15px] font-semibold text-gray-900">
                  #{orderData.orderId || Math.random().toString(36).substr(2, 9).toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-[15px] text-[#6D6D72]">Restaurant</span>
                <span className="text-[15px] font-semibold text-gray-900">{orderData.restaurantName || 'CheF'}</span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-[15px] text-[#6D6D72]">Order Type</span>
                <span className="text-[15px] font-semibold text-gray-900 capitalize">{orderData.orderType || 'Pickup'}</span>
              </div>
              {orderData.orderType === 'delivery' && orderData.deliveryAddress && (
                <div className="flex justify-between gap-4 px-4 py-3">
                  <span className="text-[15px] text-[#6D6D72] shrink-0">Delivery Address</span>
                  <span className="text-[15px] font-semibold text-gray-900 text-right">
                    {orderData.deliveryAddress.address}
                  </span>
                </div>
              )}
              <div className="flex justify-between px-4 py-3">
                <span className="text-[15px] text-[#6D6D72]">Total Amount</span>
                <span className="text-[17px] font-bold text-gray-900">{formatCurrencyXCD(orderData.totalAmount || 0)}</span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-[15px] text-[#6D6D72]">Payment Method</span>
                <span className="text-[15px] font-semibold text-gray-900">Card</span>
              </div>
            </div>
          </div>
        )}

        {/* What's Next card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <h3 className="text-[17px] font-semibold text-gray-900 px-4 pt-4 pb-2">What's Next?</h3>
          <div className="divide-y divide-black/[0.06]">
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="w-[30px] h-[30px] rounded-[7px] bg-[#007AFF] flex items-center justify-center shrink-0">
                <Smartphone size={17} className="text-white" />
              </span>
              <span className="text-[15px] text-gray-900">You'll receive updates on your order status</span>
            </div>
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="w-[30px] h-[30px] rounded-[7px] bg-[#FF9500] flex items-center justify-center shrink-0">
                <Clock size={17} className="text-white" />
              </span>
              <span className="text-[15px] text-gray-900">Estimated preparation time: 15-20 minutes</span>
            </div>
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="w-[30px] h-[30px] rounded-[7px] bg-[#34C759] flex items-center justify-center shrink-0">
                <Truck size={17} className="text-white" />
              </span>
              <span className="text-[15px] text-gray-900">
                {orderData?.orderType === 'delivery'
                  ? 'Driver will be assigned when order is ready'
                  : 'Order will be ready for pickup'}
              </span>
            </div>
          </div>
        </div>

        {/* Action buttons — red primary + outlined secondary */}
        <div className="space-y-3 pt-2">
          <button
            onClick={() => navigate('/orders')}
            className="w-full bg-[#FF3B30] text-white text-[17px] font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 active:opacity-80 transition-opacity"
          >
            <Receipt size={20} />
            View Orders
          </button>

          <button
            onClick={() => navigate('/menu')}
            className="w-full bg-white text-[#FF3B30] text-[17px] font-semibold py-3.5 rounded-xl border border-[#FF3B30] flex items-center justify-center gap-2 active:bg-[#FF3B30]/5 transition-colors"
          >
            <Home size={20} />
            Back to Menu
          </button>
        </div>

        {/* Need Help card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <h3 className="text-[17px] font-semibold text-gray-900 px-4 pt-4 pb-2">Need Help?</h3>
          <div className="divide-y divide-black/[0.06]">
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="w-[30px] h-[30px] rounded-[7px] bg-[#FF3B30] flex items-center justify-center shrink-0">
                <Phone size={17} className="text-white" />
              </span>
              <div>
                <p className="text-[15px] font-semibold text-gray-900">Call Us</p>
                <p className="text-[13px] text-[#6D6D72]">1 869 660 1052</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="w-[30px] h-[30px] rounded-[7px] bg-[#FF3B30] flex items-center justify-center shrink-0">
                <Mail size={17} className="text-white" />
              </span>
              <div>
                <p className="text-[15px] font-semibold text-gray-900">Email Support</p>
                <p className="text-[13px] text-[#6D6D72]">browneinc7@gmail.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
